import { TransactionTypeEnum, ValidatorsProps } from '@ui/src';

export enum BlackListStatus {
  ACTIVE = 'Active',
  DISABLED = 'Disabled',
}

export interface BlacklistSettings {
  corporateClients: boolean;
  corporateClientsAdverseMedia: boolean;
  corporateClientsEnforcement: boolean;
  corporateClientsPEP: boolean;
  corporateClientsSOESWF: boolean;
  corporateClientsSanction: boolean;
  individualClients: boolean;
  individualClientsAdverseMedia: boolean;
  individualClientsEnforcement: boolean;
  individualClientsPEP: boolean;
  individualClientsSOESWF: boolean;
  individualClientsSanction: boolean;
  paymentOrderPayersPayees: boolean;
  paymentOrderPayersPayeesAdverseMedia: boolean;
  paymentOrderPayersPayeesEnforcement: boolean;
  paymentOrderPayersPayeesPEP: boolean;
  paymentOrderPayersPayeesSOESWF: boolean;
  paymentOrderPayersPayeesSanction: boolean;
  transactionsCorporateClients: boolean;
  transactionsCorporateClientsAdverseMedia: boolean;
  transactionsCorporateClientsEnforcement: boolean;
  transactionsCorporateClientsPEP: boolean;
  transactionsCorporateClientsSOESWF: boolean;
  transactionsCorporateClientsSanction: boolean;
  transactionsCorporatePayersPayees: boolean;
  transactionsCorporatePayersPayeesAdverseMedia: boolean;
  transactionsCorporatePayersPayeesEnforcement: boolean;
  transactionsCorporatePayersPayeesPEP: boolean;
  transactionsCorporatePayersPayeesSOESWF: boolean;
  transactionsCorporatePayersPayeesSanction: boolean;
  transactionsCounterpartiesBanks: boolean;
  transactionsCounterpartiesBanksAdverseMedia: boolean;
  transactionsCounterpartiesBanksEnforcement: boolean;
  transactionsCounterpartiesBanksPEP: boolean;
  transactionsCounterpartiesBanksSOESWF: boolean;
  transactionsCounterpartiesBanksSanction: boolean;
  transactionsIndividualClients: boolean;
  transactionsIndividualClientsAdverseMedia: boolean;
  transactionsIndividualClientsEnforcement: boolean;
  transactionsIndividualClientsPEP: boolean;
  transactionsIndividualClientsSOESWF: boolean;
  transactionsIndividualClientsSanction: boolean;
  transactionsIndividualPayersPayees: boolean;
  transactionsIndividualPayersPayeesAdverseMedia: boolean;
  transactionsIndividualPayersPayeesEnforcement: boolean;
  transactionsIndividualPayersPayeesPEP: boolean;
  transactionsIndividualPayersPayeesSOESWF: boolean;
  transactionsIndividualPayersPayeesSanction: boolean;
}

export interface Blacklist {
  id: string;
  name: string;
  active: boolean;
  description?: string;
  successfulSyncLastDate: string;
  lastTrySyncDate: string;
  nextTrySyncDate: string;
  url?: string;
  source?: string;
  settings?: BlacklistSettings & {
    id: string;
    blacklistId: string;
  };
  data: any[];
  createdAt: string;
  updatedAt: string;
  default?: boolean;
  link?: string;
  render?: Record<string, React.JSX.Element>;
}

export enum GenderEnum {
  MALE = 'MALE',
  FEMALE = 'FEMALE',
}

export interface EntityAddress {
  id: string;
  address1: string;
  address2: string;
  addressTypeDesc: string;
  city: string;
  country: string;
  entityAddressGUID: string;
  isoStandard: string;
  lastUpdated: string;
  postalCode: string;
  stateProvinceRegion: string;
}

export interface EntityAliases {
  aliasTypeDesc: string;
  englishDescription: string;
  entityAliasGUID: string;
  firstName: string;
  lastName: string;
  lastUpdated: string;
  middleName: string;
  name: string;
  prefix: string;
  suffix: string;
  id: string;
}

export interface EntityCountryAssociations {
  id: string;
  administrativeUnitName: string;
  associationTypeDesc: string;
  entityCountryAssociationGUID: string;
  isoStandard: string;
  lastUpdated: string;
  ownershipPercentageCalc: string | number;
}

export interface EntityIdentifications {
  id: string;
  administrativeUnitName: string;
  entityIdentificationGUID: string;
  expirationDate?: {
    expirationDay: string;
    expirationMonth: string;
    expirationYear: string;
  };
  expirationDateValue?: string;
  identificationIssuer: string;
  identificationNumber: string;
  identificationTypeDesc: string;
  isoStandard: string;
  issueDate?: {
    issueDay: string;
    issueMonth: string;
    issueYear: string;
  };
  issueDateValue?: string;
  lastUpdated?: string;
}

export interface EntityPositions {
  id: string;
  entityPositionGUID: string;
  lastUpdated: string;
  position: string;
}

export interface EntityRelationships {
  id: string;
  entityRelationshipGUID: string;
  groupDesc: string;
  lastUpdated: string;
  ownershipPercentage: string;
  relatedEntityGUID: string;
  relationshipDesc: string;
  render?: Record<string, React.JSX.Element>;
}

export interface EntityRemarks {
  entityRemarkGUID: string;
  lastUpdated: string;
  remark: string;
}

export interface EntitySourceItems {
  entitySourceItemGUID: string;
  lastUpdated: string;
  sourceURI: string;
}

export interface EntityAdverseMedias {
  adverseMediaDesc: string;
  entityAdverseMediaGUID: string;
  entityAdverseMediaSubcategories: {
    entityAdverseMediaSubCategoryGUID: string;
    lastUpdated: string;
    subCategoryLabel: string;
  }[];
  subCategories?: string;
  lastUpdated: string;
}

export interface EntityEnforcements {
  administrativeUnitName: string;
  enforcementDesc: string;
  entityEnforcementGUID: string;
  entityEnforcementSubcategories: {
    entityEnforcementSubCategoryGUID: string;
    lastUpdated: string;
    subCategoryLabel: string;
  }[];
  subCategories?: string;
  isoStandard: string;
  lastUpdated: string;
  sourceName: string;
  sourceNameAbbrev: string;
}

export interface EntityPEP {
  id: string;
  administrativeUnitLevel0: string;
  administrativeUnitLevel1: string;
  administrativeUnitLevel2: string;
  administrativeUnitLevel3: string;
  administrativeUnitLevel4: string;
  effectiveDate: {
    effectiveDay: string;
    effectiveMonth: string;
    effectiveYear: string;
  };
  effectiveDateValue?: string;
  effectiveDateTypeDesc: string;
  entityPEPGUID: string;
  entityPEPSubcategories: {
    entityPEPSubCategoryGUID: string;
    lastUpdated: string;
    subCategoryDesc: string;
    subCategoryLabel: string;
  }[];
  expirationDate: {
    expirationDay: string;
    expirationMonth: string;
    expirationYear: string;
  };
  expirationDateValue?: string;
  expirationDateTypeDesc: string;
  governingInstitution: string;
  governingRole: string;
  isActivePEP: string;
  isInCountryPEPOnly: string;
  isPrimaryPEP: string;
  isoAdministrativeUnitLevel0: string;
  lastUpdated: string;
  pepAdminLevelDesc: string;
  flags?: string;
}

export interface EntitySanctions {
  id: string;
  administrativeUnitName: string;
  consolidatedSanction?: {
    consolidatedSanctionGUID: string;
    entityGUID: string;
    lastUpdated: string;
  };
  consolidated?: string;
  entitySanctionDesignations?: {
    entitySanctionDesignationGUID: string;
    designationName: string;
    subDesignationName: string;
    entitySanctionGUID: string;
    lastUpdated: string;
  }[];
  designations?: string;
  entitySanctionGUID: string;
  isoStandard: string;
  lastUpdated: string;
  sourceName: string;
  sourceNameAbbrev: string;
}

export interface EntitySOE {
  id: string;
  entitySOEDomains: {
    entitySOEDomainGUID: string;
    lastUpdated: string;
    soeDomainDesc: string;
  }[];
  domains?: string;
  entitySOEGUID: string;
  entitySOESubcategories?: {
    entitySOESubCategoryGUID: string;
    lastUpdated: string;
    subCategoryLabel: string;
  }[];
  subCategories?: string;
  isActive: string;
  lastUpdated: string;
}

export interface AdditionalSegmentField {
  id: string;
  derivedName: string;
  derivedValue: string;
}

export interface AdditionalSegment {
  lastUpdated: string;
  segmentGUID: string;
  type: string;
  additionalSegmentFields: AdditionalSegmentField[];
}

export interface BlacklistData {
  id: string;
  primaryTerm?: any;
  score?: number;
  seqNo?: any;
  sort?: string;
  name?: string;
  createdAt?: string;
  lastUpdated?: string;
  successfulSyncLastDate?: string;
  source: {
    createdAt?: string;
    lastUpdated?: string;
    firstName: string;
    lastName: string;
    middleName?: string;
    gender?: string;
    blacklistId: string;
    entityTypeDesc?: string;
    name: string;
    entityGUID?: string;
    entityID?: string;
    lookupID?: string;
    isDeceased?: string;
    entityAddresses?: EntityAddress[];
    entityAliases?: EntityAliases[];
    entityCountryAssociations: EntityCountryAssociations[];
    entityDOBs?: {
      birthDate: {
        birthDay: string;
        birthMonth: string;
        birthYear: string;
      };
      entityDOBGUID: string;
      lastUpdated: string;
    }[];
    entityIdentifications?: EntityIdentifications[];
    entityPositions?: EntityPositions[];
    entityRelationships?: EntityRelationships[];
    entityRemarks?: EntityRemarks[];
    entitySourceItems?: EntitySourceItems[];
    entitySegments?: {
      entityAdverseMedias?: EntityAdverseMedias[];
      entityEnforcements?: EntityEnforcements[];
      entityPEPs?: EntityPEP[];
      entitySanctions?: EntitySanctions[];
      entitySOEs?: EntitySOE[];
      additionalSegments?: AdditionalSegment[];
    };
    prefix?: string;
    suffix?: string;
    title?: string;
  };
  url: string;
  render?: Record<string, React.JSX.Element>;
}

export interface BlacklistValidators {
  updateBlacklistSchema: ValidatorsProps;
  createBlacklistSchema: ValidatorsProps;
  updateBlacklistSettingsSchema: ValidatorsProps;
}

export interface BlackListItem {
  id: string;
  name: string;
  source: string;
  dateAdded: string;
  dateUpdated: string;
  attemptUpdateDate?: string;
  status: BlackListStatus;
  default: boolean;
  render?: Record<string, React.JSX.Element>;
}

export interface BlacklistFormValues extends BlacklistSettings {
  name: string;
  description?: string;
  active: boolean;
}

export interface BlackListUser {
  id: string;
  fullName: string;
  source: string;
  dateAdded: string;
}

export interface BlacklistUserInfo {
  id: string;
  docId: string;
  dateAdded: string;
  fullName: string;
  alias: string;
  nameInNonLatinscript?: string;
  dateOfBirth?: string;
  gender?: string;
  nationality?: string;
  countryOfResidence?: string;
  countryName?: string;
  countryCode?: string;
  placeOfBirth?: string;
  address?: string;
  reason?: string;
  otherInformation?: string;
  personalIDNumber?: string;
  pspName?: string;
  pspAddress?: string;
  swiftBic?: string;
}

export interface Setting {
  id: string;
  score: {
    from: number;
    to: number;
  };
  category: string;
  check: boolean;
  dateAdded: string;
  dateUpdated: string;
  render?: Record<string, React.JSX.Element>;
}

export interface GeographicalRiskListItem {
  id: string;
  countryName: string;
  countryCode: string;
  riskScore: string;
}

export enum RiskScoreCalculationType {
  WEIGHT = 'WEIGHT',
  MIN = 'MIN',
  MAX = 'MAX',
  AVG = 'AVG',
}

export interface Factor {
  type: RiskScoreType;
  calculationType: RiskScoreCalculationType;
  description: string;
  weight: number;
  factorId: string;
  id: string;
}

export interface GeographicalRiskFormValues {
  transactionsCalculate: RiskScoreCalculationType | undefined;
  transactionsСountryOfTheСounterparty: string;
  transactionBank: string;
  clientsCalculate: RiskScoreCalculationType | undefined;
  clientsNationality: string;
  clientsResidence: string;
}

export interface TransactionVolumeRiskFormValues {
  individualClientsCalculate: RiskScoreCalculationType | undefined;
  individualClientsSingleTransactionVolume: string;
  individualClientsMultipleTransactionVolume: string;
  corporateClientsCalculate: RiskScoreCalculationType | undefined;
  corporateClientsSingleTransactionVolume: string;
  corporateClientsMultipleTransactionVolume: string;
}

export interface TransactionVolumeRiskListItem {
  id: string;
  riskCategory: string;
  score: number;
  individualSingleTransaction?: {
    id?: string;
    from: number;
    to: number;
    currency: string;
  };
  individualMultiTransaction?: {
    id?: string;
    from: number;
    to: number;
    currency: string;
  };
  corporateSingleTransacion?: {
    id?: string;
    from: number;
    to: number;
    currency: string;
  };
  corporateMultiTransaction?: {
    id?: string;
    from: number;
    to: number;
    currency: string;
  };
}

export interface ProductRiskListItem {
  id: string;
  name: string;
  riskScore: number;
  riskCategory: string;
  updatedAt: string;
  scoreAction: string;
  riskScoreRangeId: string;
  url?: string;
  render?: Record<string, React.JSX.Element>;
}

export enum ClientType {
  INDIVIDUAL = 'INDIVIDUAL',
  COMPANY_OFFICIAL = 'COMPANY_OFFICIAL',
  CORPORATE = 'CORPORATE',
  INDIVIDUAL_COMPANY_OFFICIAL = 'INDIVIDUAL_COMPANY_OFFICIAL',
}

export enum CounterPartyEnum {
  ANY = 'ANY',
  SAME = 'SAME',
  NEW = 'NEW',
}

export enum TransactionMonitoringRuleEntity {
  TRANSACTION = 'TRANSACTION',
  PAYMENT_ORDER = 'PAYMENT_ORDER',
}
export interface TransactionMonitoringRule {
  transactionTypeId: string;
  transactionType: {
    name:
      | TransactionTypeEnum.INTERNAL
      | TransactionTypeEnum.INCOMING
      | TransactionTypeEnum.OUTGOING;
  };
  name: string;
  number: number;
  volumeFrom: number;
  volumeTo: number;
  periodOfTimeInHours: number;
  counterParty: CounterPartyEnum;
  clientType: ClientType;
  active: boolean;
  action: string;
  id: string;
  url?: string;
  createdAt: string;
  entity?: TransactionMonitoringRuleEntity;
  paymentOrderType?: string;
  paymentType?: string;
  clientRiskScoreRangeIds?: string[];
  organizationIds?: string[];
  counterpartyCountryIds?: string[];
  counterpartyCountryRiskScoreRangeIds?: string[];
  counterpartyBankCountryIds?: string[];
  counterpartyBankCountryRiskScoreRangeIds?: string[];
  currencyIds?: string[];
  singleTransactionAmountFrom?: string | number | null;
  singleTransactionAmountTo?: string | number | null;
  numberTo?: number;
  render?: Record<string, React.JSX.Element>;
}

export interface TransactionMonitoringRulesValidators {
  toggleActiveSchema: ValidatorsProps;
  updateMonitoringRuleSchema: ValidatorsProps;
  createMonitoringRuleSchema: ValidatorsProps;
}

export interface TransactionMonitoringRuleFormValues {
  transactionTypeId?: string;
  clientType: ClientType;
  transactionType?: string;
  counterParty: string;
  name: string;
  number?: number | string | null;
  volumeFrom: number;
  volumeTo: number;
  action: string;
  periodOfTimeInHours: string | number;
  active?: boolean;
  paymentOrderType?: string;
  entity?: string;
  paymentType?: string | null;
  clientRiskScoreRangeIds?: string[];
  organizationIds?: string[];
  counterpartyCountryIds?: string[];
  counterpartyCountryRiskScoreRangeIds?: string[];
  counterpartyBankCountryIds?: string[];
  counterpartyBankCountryRiskScoreRangeIds?: string[];
  currencyIds?: string[];
  singleTransactionAmountFrom?: string | number | null;
  singleTransactionAmountTo?: string | number | null;
  numberTo?: string | number | null;
}

export interface SicCode {
  description: string;
  code: string;
  riskScore: number;
  id: string;
  url?: string;
  render?: Record<string, React.JSX.Element>;
}

export interface RiskScoreItem {
  riskScoreRange: {
    name: string;
    rangeStart: number;
    rangeEnd: number;
    action: string;
    id: string;
  };
  url?: string;
  name: string;
  riskScoreRangeId: string;
  riskScore: number;
  fromYear: number;
  id: string;
  updatedAt: string;
  render?: Record<string, React.JSX.Element>;
}

export enum CategoryLevelEnum {
  HIGH = 'HIGH',
  MEDIUM = 'MEDIUM',
  LOW = 'LOW',
}

export enum ScoreActionEnum {
  OPS_REVIEW = 'OPS_REVIEW',
  AML_REVIEW = 'AML_REVIEW',
  PASS = 'PASS',
}

export enum PeriodUnitEnum {
  HOURS = 'HOURS',
  DAYS = 'DAYS',
  WEEKS = 'WEEKS',
  MONTH = 'MONTH',
}

export interface IncorporationDateListItem {
  id: string;
  fromYearStart: string;
  fromYear: number;
  riskScore: number;
  riskCategory: string;
  scoreAction: string;
  url?: string;
  render?: Record<string, React.JSX.Element>;
}

export interface DeliveryChannel {
  id: string;
  name: string;
  riskScore: number;
  scoreAction: string;
  riskCategory: string;
  url?: string;
  riskScoreRangeId: string;
  render?: Record<string, React.JSX.Element>;
}

export interface SourceOfFunds {
  id: string;
  name: string;
  riskScore: number;
  scoreAction: string;
  riskCategory: string;
  riskScoreRangeId: string;
  url?: string;
  render?: Record<string, React.JSX.Element>;
}

export interface ClientsActivity {
  id: string;
  name: string;
  riskScore: number;
  scoreAction: string;
  riskCategory: string;
  riskScoreRangeId: string;
  url?: string;
  render?: Record<string, React.JSX.Element>;
}

export interface ClientRange {
  type: string;
  from: number;
  id: string;
  riskScore: number;
  to?: number;
  url?: string;
  active?: boolean;
}

export enum SettingClientRangeType {
  MONTHLY_TURNOVER = 'MONTHLY_TURNOVER',
  MONTHLY_NUMBER_OF_INCOMING_TRANSACTIONS = 'MONTHLY_NUMBER_OF_INCOMING_TRANSACTIONS',
  MONTHLY_NUMBER_OF_OUTGOING_TRANSACTIONS = 'MONTHLY_NUMBER_OF_OUTGOING_TRANSACTIONS',
  MONTHLY_VALUE_OF_INCOMING_TRANSACTIONS = 'MONTHLY_VALUE_OF_INCOMING_TRANSACTIONS',
  MONTHLY_VALUE_OF_OUTGOING_TRANSACTIONS = 'MONTHLY_VALUE_OF_OUTGOING_TRANSACTIONS',
  MONTHLY_MAX_VALUE_OF_INCOMING_PAYMENT = 'MONTHLY_MAX_VALUE_OF_INCOMING_PAYMENT',
  MONTHLY_MAX_VALUE_OF_OUTGOING_PAYMENT = 'MONTHLY_MAX_VALUE_OF_OUTGOING_PAYMENT',
}

export interface VolumesFormValues {
  from: string;
  to: string;
  riskScore: string;
  riskCategory: string;
  type: string;
}

export interface Turnover {
  id: string;
  to?: number;
  name: string;
  from: number;
  riskCategory: string;
  riskScore: number;
  type: string;
  url?: string;
  active?: boolean;
  render?: Record<string, React.JSX.Element>;
}

export interface DeliveryChannelValidators {
  createDeliveryChannelSchema: ValidatorsProps;
  updateDeliveryChannelSchema: ValidatorsProps;
}

export interface DeliveryChannelFormValues {
  name: string;
  riskScoreRangeId: string;
  riskScore: number;
  clientType: string;
}

export enum RiskScoreClientTypeEnum {
  INDIVIDUAL = 'INDIVIDUAL',
  CORPORATE = 'CORPORATE',
}

export interface ClientActivitiesValidators {
  createClientActivitySchema: ValidatorsProps;
  updateClientActivitySchema: ValidatorsProps;
}

export interface ClientActivityFormValues {
  name: string;
  riskScoreRangeId: string;
  riskScore: number;
}

export interface SourceOfFundsValidators {
  createSourceOfFundsSchema: ValidatorsProps;
  updateSourceOfFundsSchema: ValidatorsProps;
}

export interface SourceOfFundsFormValues {
  name: string;
  riskScoreRangeId: string;
  riskScore: number;
}

export interface ProductsValidators {
  createProductSchema: ValidatorsProps;
  updateProductSchema: ValidatorsProps;
}

export interface ProductFormValues {
  name: string;
  riskScoreRangeId: string;
  riskScore: number;
  clientType: string;
}

export enum SettingsValueEnum {
  WEIGHT = 'WEIGHT',
  MAX_VALUE = 'MAX_VALUE',
}

export enum RangeType {
  NONE = 'NONE',
  OPS_REVIEW = 'OPS_REVIEW',
  AML_REVIEW = 'AML_REVIEW',
}

export interface RiskScoreRange {
  name: string;
  rangeStart: number;
  rangeEnd: number;
  action: RangeType;
  id: string;
  url?: string;
  updatedAt: string;
  createdAt: string;
  render?: Record<string, React.JSX.Element>;
}

export interface TransactionTypeFormValues {
  riskScore: string;
}

export interface RiskScoreRangeFormValues {
  name: string;
  rangeStart: string;
  rangeEnd: string;
  action: string;
}

export interface IncorporationDateFormValues {
  fromYear: number;
  riskScoreRangeId: string;
  riskScore: number;
}

export enum RiskScoreType {
  COUNTRY_OF_THE_COUNTERPARTY = 'COUNTRY_OF_THE_COUNTERPARTY',
  COUNTRY_OF_THE_BANK_OF_COUNTERPARTY = 'COUNTRY_OF_THE_BANK_OF_COUNTERPARTY',
  TRANSACTION = 'TRANSACTION',
  SINGLE_TRANSACTION = 'SINGLE_TRANSACTION',
  MULTIPLE_TRANSACTIONS = 'MULTIPLE_TRANSACTIONS',
  CORPORATE_TRANSACTION = 'CORPORATE_TRANSACTION',
  CORPORATE_SINGLE_TRANSACTION = 'CORPORATE_SINGLE_TRANSACTION',
  CORPORATE_MULTIPLE_TRANSACTIONS = 'CORPORATE_MULTIPLE_TRANSACTIONS',
  PERSONAL_TRANSACTION = 'PERSONAL_TRANSACTION',
  PERSONAL_SINGLE_TRANSACTION = 'PERSONAL_SINGLE_TRANSACTION',
  PERSONAL_MULTIPLE_TRANSACTIONS = 'PERSONAL_MULTIPLE_TRANSACTIONS',
  COUNTRY = 'COUNTRY',
  TRANSACTION_CALCULATION_RULE = 'TRANSACTION_CALCULATION_RULE',
  PERSONAL_PRODUCT = 'PERSONAL_PRODUCT',
  PERSONAL_SOURCE_OF_FUNDS = 'PERSONAL_SOURCE_OF_FUNDS',
  PERSONAL_GEOGRAPHY = 'PERSONAL_GEOGRAPHY',
  PERSONAL_GEOGRAPHY_RESIDENCY = 'PERSONAL_GEOGRAPHY_RESIDENCY',
  PERSONAL_GEOGRAPHY_NATIONALITY = 'PERSONAL_GEOGRAPHY_NATIONALITY',
  CORPORATE_PRODUCT = 'CORPORATE_PRODUCT',
  CORPORATE_GEOGRAPHY = 'CORPORATE_GEOGRAPHY',
  CORPORATE_GEOGRAPHY_DIRECOTRS = 'CORPORATE_GEOGRAPHY_DIRECOTRS',
  CORPORATE_GEOGRAPHY_CORPORATION = 'CORPORATE_GEOGRAPHY_CORPORATION',
}

export enum PaymentTypeEnum {
  FASTER_PAYMENT = 'FASTER_PAYMENT',
  BACS = 'BACS',
  SEPA_SCT_INST = 'SEPA_SCT_INST',
  SEPA_SCT = 'SEPA_SCT',
  TARGET_2 = 'TARGET_2',
  FEDWIRE = 'FEDWIRE',
  CRYPTO_ASSET_PAYMENT = 'CRYPTO_ASSET_PAYMENT',
  SWIFT = 'SWIFT',
  CASH_OVER_COUNTER = 'CASH_OVER_COUNTER',
}

export interface TransactionTypeItem {
  name: TransactionTypeEnum;
  transactionType: {
    name: TransactionTypeEnum;
  };
  paymentType?: string;
  active: boolean;
  riskScore: number;
  riskCategory?: string;
  id: string;
  updatedAt: string;
  render?: Record<string, React.JSX.Element>;
}

export enum TransactionVolumeTypeEnum {
  SINGLE = 'SINGLE',
  MULTIPLE = 'MULTIPLE',
}

export interface TransactionVolume {
  id: string;
  createdAt: string;
  updatedAt: string;
  min: number;
  max: number;
  riskScore: number;
  type: TransactionVolumeTypeEnum;
  customer: ClientType;
  currency: string;
  riskCategory: string;
}

export interface TransactionVolumeFormValues {
  type: string;
  customer: string;
  min: number;
  max?: number;
  riskScore: number;
  currency: string;
  riskCategory: string;
}

export interface TrasnactionsRiskFormValues {
  riskScore: string;
  riskCategory: string;
  individualSingleTransactionFrom: string;
  individualSingleTransactionId?: string;
  individualSingleTransactionTo: string;
  individualMultiTransactionFrom: string;
  individualMultiTransactionId?: string;
  individualMultiTransactionTo: string;
  corporateSingleTransactionFrom: string;
  corporateSingleTransactionId?: string;
  corporateSingleTransactionTo: string;
  corporateMultiTransactionFrom: string;
  corporateMultiTransactionId?: string;
  corporateMultiTransactionTo: string;
  currencyId: string;
}

export interface ClientRangeFormValues {
  type: string;
  from: string | number;
  riskScore: string | number;
  clientType: string;
  active?: boolean;
  riskCategory?: string;
}

export interface TransactionsTypesRiskScore {
  createdAt: string;
  id: string;
  paymentType: string;
  riskScore: number;
  transactionTypeId: string;
  transactionType: {
    id: string;
    name: string;
  };
  render?: Record<string, React.JSX.Element>;
}

export interface AddRecordFormValues {
  entityDOBs?: string[];
  entityTypeDesc: string;
  gender?: string;
  name?: string;
  firstName?: string;
  lastName?: string;
  middleName?: string;
  prefix?: string;
  suffix?: string;
  title?: string;
}

export interface EntityAddressesFormValues {
  id: string;
  addressTypeDesc?: string;
  address1?: string;
  address2?: string;
  city?: string;
  postalCode?: string;
  country?: string;
  isoStandard?: string;
}

export interface EntityAliasesFormValues {
  id: string;
  aliasTypeDesc?: string;
  englishDescription?: string;
  name?: string;
  firstName?: string;
  lastName?: string;
  middleName?: string;
}

export interface EntityIdentificationsFormValues {
  id: string;
  administrativeUnitName?: string;
  isoStandard?: string;
  identificationIssuer?: string;
  identificationTypeDesc?: string;
  identificationNumber?: string;
  issueDateValue?: string;
  issueDate?: {
    issueYear: string;
    issueMonth: string;
    issueDay: string;
  };
  expirationDateValue?: string;
  expirationDate?: {
    expirationYear: string;
    expirationMonth: string;
    expirationDay: string;
  };
}

export interface EntityPositionsFormValues {
  id: string;
  position?: string;
}

export interface RecordItem {
  entityDOBs?: {
    birthDate: {
      birthYear: string;
      birthMonth: string;
      birthDay: string;
    };
  }[];
  entityAddresses?: EntityAddressesFormValues[];
  entityAliases?: EntityAliasesFormValues[];
  entityIdentifications?: EntityIdentificationsFormValues[];
  entityPositions?: EntityPositionsFormValues[];
  entityTypeDesc?: string;
  gender?: string;
  name?: string;
  firstName?: string;
  lastName?: string;
  middleName?: string;
  prefix?: string;
  suffix?: string;
  title?: string;
}

export interface Organization {
  id: string;
  firstName?: string;
  lastName?: string;
  companyName?: string;
  type: string;
  user?: {
    phone?: string;
  };
  companyNumber?: string;
}
